/**
 * Invoice detail > Online payments (tracker item 38): charge a card on file
 * of the billing customer (choose the card, confirm the amount), the last
 * online attempt's status, and the invoice's online card payments with a
 * Refund action (ADMIN). Office roles only (the page decides).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IOnlinePaymentsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, rowDangerActionClass, secondaryButtonClass } from '../../components/formStyles';
import { AttemptStatusBadge, RefundForm } from './OnlinePaymentWidgets';
import { amountError, cardLabel, formatUsd, newIdempotencyKey, refundPendingVoid } from './onlinePaymentsUi';
import { Toast } from '../../components/Toast';

interface Props {
  invoice: types.Invoice;
  /** Balance still due (string, 2 dp) */
  balanceDue: string;
  canRefund: boolean;
  onChanged: () => void;
}

export function InvoiceOnlinePaymentsPanel({ invoice, balanceDue, canRefund, onChanged }: Props) {
  const api = useMemo<IOnlinePaymentsApi>(() => createAPIClient(), []);
  const [methods, setMethods] = useState<types.CustomerPaymentMethod[]>([]);
  const [attempts, setAttempts] = useState<types.OnlinePaymentAttempt[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [charging, setCharging] = useState(false);
  const [methodId, setMethodId] = useState('');
  const [amount, setAmount] = useState(balanceDue);
  const [confirming, setConfirming] = useState(false);
  const [chargeKey, setChargeKey] = useState('');
  const [refundFor, setRefundFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [cards, list, settings] = await Promise.all([
        api.listPaymentMethods(invoice.billingCustomerId),
        api.listOnlinePaymentAttempts({ invoiceId: invoice.id }, 1, 20),
        api.getOnlinePaymentSettings().catch(() => null),
      ]);
      const active = cards.filter((c) => c.status === 'ACTIVE');
      setMethods(active);
      setMethodId((current) => current || active.find((c) => c.isDefault)?.id || active[0]?.id || '');
      setAttempts(list.data);
      setEnabled(!!settings?.enabled);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load online payments'));
    }
  }, [api, invoice.billingCustomerId, invoice.id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setAmount(balanceDue); }, [balanceDue]);

  const payable = [types.InvoiceStatus.POSTED, types.InvoiceStatus.PARTIAL].includes(invoice.status);
  const last = attempts[0];
  const paid = attempts.filter((a) => a.paymentId && (a.status === 'RECORDED' || refundPendingVoid(a)));
  const problem = amountError(amount, '0.01', balanceDue);
  const method = methods.find((m) => m.id === methodId);

  if (!payable && attempts.length === 0) return null;

  async function charge() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.chargeInvoiceCard(invoice.id, { paymentMethodId: methodId, amount: Number(amount).toFixed(2), idempotencyKey: chargeKey });
      const a = result.attempt;
      if (a.status === 'RECORDED') setNotice(`Charged ${formatUsd(a.amount)} to ${cardLabel({ brand: a.cardBrand, last4: a.cardLast4 })}.`);
      else if (a.status === 'DECLINED') setError(a.failureMessage ?? 'The card was declined.');
      else setError(a.failureMessage ?? 'The charge could not be confirmed; check Payments > Online payments before charging again.');
      setConfirming(false);
      setCharging(false);
      await load();
      onChanged();
    } catch (cause) {
      setError(getErrorMessage(cause, 'The card could not be charged'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="invoice-online-heading" className="bg-white rounded-lg shadow p-5 mb-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="invoice-online-heading" className="font-semibold text-lg">Online payments</h2>
        {payable && enabled && methods.length > 0 && !charging && (
          <button type="button" className={secondaryButtonClass} onClick={() => { setCharging(true); setConfirming(false); setChargeKey(newIdempotencyKey()); setAmount(balanceDue); }}>
            Charge card on file
          </button>
        )}
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      {last && (
        <p className="text-sm text-gray-700" data-testid="last-attempt">
          Last online attempt: <AttemptStatusBadge status={last.status} /> {formatUsd(last.amount)} on {last.createdAt.slice(0, 10)}
          {last.failureMessage && last.status !== 'RECORDED' && <span className="block text-xs text-gray-600">{last.failureMessage}</span>}
        </p>
      )}
      {payable && enabled && methods.length === 0 && <p className="text-sm text-gray-600">No card on file for this customer (add one on the customer page).</p>}
      {payable && !enabled && <p className="text-sm text-gray-600">Online payments are off (Settings &gt; Payments).</p>}
      {charging && (
        <div className="rounded border border-gray-200 p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="charge-card" className={labelClass}>Card</label>
              <select id="charge-card" className={inputClass} value={methodId} disabled={busy || confirming} onChange={(e) => setMethodId(e.target.value)}>
                {methods.map((m) => <option key={m.id} value={m.id}>{cardLabel(m)}{m.isDefault ? ' (default)' : ''}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="charge-amount" className={labelClass}>Amount</label>
              <input id="charge-amount" inputMode="decimal" className={inputClass} value={amount} disabled={busy || confirming}
                onChange={(e) => setAmount(e.target.value)} aria-invalid={!!problem} />
              {problem && <p className="mt-1 text-xs text-red-700">{problem}</p>}
            </div>
          </div>
          {confirming ? (
            <div role="alertdialog" aria-label="Confirm charge" className="space-y-2">
              <p className="text-sm">Charge <strong>{formatUsd(amount)}</strong> to {cardLabel(method)} now?</p>
              <div className="flex flex-wrap gap-3">
                <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void charge()}>{busy ? 'Charging…' : `Charge ${formatUsd(amount)}`}</button>
                <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirming(false)}>Back</button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              <button type="button" className={primaryButtonClass} disabled={!!problem || !methodId} onClick={() => setConfirming(true)}>Continue</button>
              <button type="button" className={secondaryButtonClass} onClick={() => setCharging(false)}>Cancel</button>
            </div>
          )}
        </div>
      )}
      {paid.length > 0 && (
        <ul className="divide-y divide-gray-100 text-sm">
          {paid.map((a) => (
            <li key={a.id} className="py-2 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <Link className="text-blue-700 hover:underline" to={`/payments/${a.paymentId}`}>{formatUsd(a.amount)} by {cardLabel({ brand: a.cardBrand, last4: a.cardLast4 })}</Link>
                <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs text-indigo-800">{a.paymentStatus === 'VOIDED' ? 'Refunded online' : 'Charged online'}</span>
                {canRefund && a.paymentStatus === 'ACTIVE' && refundFor !== a.id && (
                  <button type="button" className={rowDangerActionClass} onClick={() => setRefundFor(a.id)}>{refundPendingVoid(a) ? 'Finish refund' : 'Refund'}</button>
                )}
              </div>
              {refundFor === a.id && a.paymentId && (
                <RefundForm
                  amount={a.amount}
                  finishOnly={refundPendingVoid(a)}
                  onCancel={() => setRefundFor(null)}
                  onRefund={async (reason) => {
                    await api.refundPayment(a.paymentId!, reason);
                    setRefundFor(null);
                    setNotice('Refunded and voided.');
                    await load();
                    onChanged();
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
